import { yupResolver } from '@hookform/resolvers/yup';
import { data as availableCurrencies } from 'currency-codes';
import { isNil } from 'lodash-es';
import type React from 'react';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { IoIosPerson } from 'react-icons/io';
import { MdEdit, MdEditOff, MdOutlineDelete } from 'react-icons/md';
import { TbPhotoEdit } from 'react-icons/tb';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router';
import Select from 'react-select';
import { useDebounceCallback } from 'usehooks-ts';
import { object, string } from 'yup';
import { ROUTES } from '../../../constants/Constants';
import { lazyReactNaiveRetry } from '../../../constants/Lazy';
import useActionModal from '../../../hooks/useActionModal';
import useAppNavigation from '../../../hooks/useAppNavigation';
import useChangePasswordModal from '../../../hooks/useChangePasswordModal';
import useDeleteAccountModal from '../../../hooks/useDeleteAccountModal';
import useProfilePictureModal from '../../../hooks/useProfilePictureModal';
import useTimezone from '../../../hooks/useTimezone';
import useUser from '../../../hooks/useUser';
import type { UpdateProfileRequestType } from '../../../proxies/UserProxy';
import { ActionModalActionType } from '../../../redux/ducks/UIDucks.types';
import {
  userDeleteProfilePictureRequest,
  userGetProfile,
  userUpdateUserProfileRequest,
} from '../../../redux/ducks/UserDucks';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import type { AppSsrFetchedDataType } from '../../../server/AppSsr';
import { getEnvironmentVariable } from '../../../utils/EnvironmentHelper/EnvironmentHelper';
import Button from '../../button/Button';
import Input from '../../inputs/Input';
import LazyBackground from '../../layout/LazyBackground/LazyBackground';
import MainLayout from '../../layout/MainLayout/MainLayout';
import Spinner from '../../layout/Spinner/Spinner';
import { PROFILE_UPDATE_DELAY_MS } from '../../modals/ProfilePictureModal';
import PreferencesBox from './PreferencesBox';

const ChangePasswordModal = lazyReactNaiveRetry(
  () => import('../../modals/ChangePasswordModal'),
);
const ProfilePictureModal = lazyReactNaiveRetry(
  () => import('../../modals/ProfilePictureModal'),
);
const DeleteAccountModal = lazyReactNaiveRetry(
  () => import('../../modals/DeleteAccountModal'),
);

const availableTimezones: string[] = Intl.supportedValuesOf('timeZone');

type EditAccoundUserInput = {
  firstName: string;
  lastName: string;
};

type SelectorOptionType = {
  id: number;
  label: string;
  value: string;
};

interface AccountProps {
  ssrFetchedData: AppSsrFetchedDataType;
}

const Account: React.FC<AccountProps> = ({ ssrFetchedData }) => {
  const { currentUser } = useUser();
  const { t } = useTranslation();
  const { toLocalizedRoute } = useAppNavigation();
  const actionModal = useActionModal();
  const dispatch = useDispatch();
  const [onEdit, setOnEdit] = useState<boolean>(false);
  const [onDeleteImage, setOnDeleteImage] = useState<boolean>(false);
  const { logout } = useUser();
  const [selectedTimezone, setSelectedTimezone] = useState<
    SelectorOptionType | undefined
  >();
  const [selectedCurrency, setSelectedCurrency] = useState<
    SelectorOptionType | undefined
  >();
  const [selectedLanguage, setSelectedLanguage] = useState<
    SelectorOptionType | undefined
  >();
  const changePasswordModal = useChangePasswordModal();
  const profilePictureModal = useProfilePictureModal();
  const deleteAccountModal = useDeleteAccountModal();
  const supportedLanguages = useSelector(
    (state: RootReducerType) => state.config.appConfig?.languages,
  );
  const { onActionModalConfirm, onActionModalCancel } = useSelector(
    (state: RootReducerType) => state.ui,
  );
  const { deleteProfilePictureResponse } = useSelector(
    (state: RootReducerType) => state.user,
  );
  const { currentTimezone } = useTimezone();

  const formSchema = object().shape({
    firstName: string()
      .required(t('static.account.validation_error_first_name_required'))
      .min(2, t('static.account.validation_error_first_name_short'))
      .max(50, t('static.account.validation_error_first_name_long')),
    lastName: string()
      .required(t('static.account.validation_error_last_name_required'))
      .min(2, t('static.account.validation_error_last_name_short'))
      .max(50, t('static.account.validation_error_last_name_long')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<EditAccoundUserInput>({
    resolver: yupResolver(formSchema),
  });

  const onClickEdit = () => {
    setOnEdit(!onEdit);
    if (!onEdit) {
      setValue('firstName', currentUser?.first_name || '');
      setValue('lastName', currentUser?.last_name || '');
    }
  };

  const onCancel = () => {
    setOnEdit(false);
  };

  const onSave = () => {
    // Dispatch update before changing onEdit otherwise the form will reset to account values
    const profileData: UpdateProfileRequestType = {
      first_name: getValues('firstName') || 'Unknown',
      last_name: getValues('lastName') || 'Unknown',
      language: selectedLanguage ? selectedLanguage.value : undefined,
      timezone: selectedTimezone ? selectedTimezone.value : currentTimezone,
      currency: selectedCurrency ? selectedCurrency.value : undefined,
    };

    dispatch(userUpdateUserProfileRequest(profileData));

    setOnEdit(false);
  };

  const onChangePassword = () => {
    setOnEdit(false);
    changePasswordModal.onOpen();
  };

  const onDeleteAccount = () => {
    setOnEdit(false);
    deleteAccountModal.onOpen();
  };

  const onLogoutAccount = () => {
    setOnEdit(false);
    logout();
  };

  const onProfilePictureClick = () => {
    setOnEdit(false);
    profilePictureModal.onOpen();
  };

  const createSelectLabel = (option: any) => (
    <div className="flex flex-row items-center gap-3">
      <div>{option.label}</div>
    </div>
  );

  const allTimezones = useMemo<SelectorOptionType[]>(
    () =>
      availableTimezones.map((tz, index) => ({
        label: tz,
        id: index + 1,
        value: tz,
      })) || [],
    [availableTimezones],
  );

  const allCurrencies = useMemo<SelectorOptionType[]>(
    () =>
      availableCurrencies.map((currencyCode, index) => ({
        label: currencyCode.code,
        id: index + 1,
        value: currencyCode.code,
      })) || [],
    [availableCurrencies],
  );

  const allLanguages = useMemo<SelectorOptionType[]>(
    () =>
      supportedLanguages
        ? supportedLanguages.map((languageCode, index) => ({
            label: t(
              `static.language.language_name_${languageCode.toLowerCase()}`,
            ),
            id: index + 1,
            value: languageCode,
          }))
        : [],
    [supportedLanguages],
  );

  useEffect(() => {
    if (currentUser && allTimezones) {
      const el = allTimezones.filter(
        (sel) => sel.value === currentUser.timezone,
      );
      if (el.length > 0) {
        setSelectedTimezone(el[0]);
      }
    }
  }, [allTimezones, currentUser, onEdit]);

  useEffect(() => {
    if (currentUser && allLanguages) {
      const el = allLanguages.filter(
        (sel) => sel.value === currentUser.language,
      );
      if (el.length > 0) {
        setSelectedLanguage(el[0]);
      }
    }
  }, [allLanguages, currentUser, onEdit]);

  useEffect(() => {
    if (currentUser && allCurrencies) {
      const el = allCurrencies.filter(
        (sel) => sel.value === currentUser.currency,
      );
      if (el.length > 0) {
        setSelectedCurrency(el[0]);
      }
    }
  }, [allCurrencies, currentUser, onEdit]);

  const onDeletePicture = useCallback(() => {
    setOnDeleteImage(true);
    actionModal.onOpen({
      action: ActionModalActionType.ConfirmDeleteProfilePicture,
      message: t('static.account.confirm_profile_image_delete'),
      cancelLabel: t(
        'static.account.confirm_profile_image_delete_cancel_label',
      ),
      confirmLabel: t(
        'static.account.confirm_profile_image_delete_confirm_label',
      ),
      confirmLabelType: 'reject',
    });
  }, [actionModal, t]);

  useEffect(() => {
    if (!onDeleteImage) {
      return;
    }

    if (!actionModal.isOpen) {
      if (onActionModalConfirm) {
        if (
          actionModal.currentAction ===
          ActionModalActionType.ConfirmDeleteProfilePicture
        ) {
          dispatch(
            userDeleteProfilePictureRequest({
              delete: true,
            }),
          );
        }
      } else if (onActionModalCancel) {
        setOnDeleteImage(false);
      }
    }
  }, [
    actionModal.isOpen,
    actionModal.currentAction,
    onActionModalConfirm,
    onActionModalCancel,
    dispatch,
    onDeleteImage,
  ]);

  const delayedProfileUpdate = useDebounceCallback(() => {
    dispatch(userGetProfile(true));
  }, PROFILE_UPDATE_DELAY_MS);

  useEffect(() => {
    if (onDeleteImage && !actionModal.isOpen && onActionModalConfirm) {
      if (!isNil(deleteProfilePictureResponse?.data)) {
        delayedProfileUpdate();
        toast.success(t('static.upload_profile_picture.api_delete_success'));
        setOnDeleteImage(false);
      } else if (!isNil(deleteProfilePictureResponse?.error)) {
        toast.error(t('static.upload_profile_picture.api_delete_failure'));
        setOnDeleteImage(false);
      }
    }
  }, [
    deleteProfilePictureResponse?.data,
    deleteProfilePictureResponse?.error,
    delayedProfileUpdate,
    onDeleteImage,
    actionModal.isOpen,
    onActionModalConfirm,
    t,
  ]);

  useEffect(() => {
    return () => {
      setOnEdit(false);
      setOnDeleteImage(false);
    };
  }, []);

  return (
    <MainLayout
      categories={false}
      search={false}
      navbar
      footer
      ssrFetchedData={ssrFetchedData}
    >
      <Suspense fallback={<div className="hidden">&nbsp;</div>}>
        <ChangePasswordModal />
      </Suspense>
      <Suspense fallback={<div className="hidden">&nbsp;</div>}>
        <ProfilePictureModal />
      </Suspense>
      <Suspense fallback={<div className="hidden">&nbsp;</div>}>
        <DeleteAccountModal />
      </Suspense>
      <div className="min-h-full min-w-full items-center justify-center px-4 pt-4">
        <div className="mb-8 flex min-w-full flex-row items-end justify-end text-right">
          {onEdit ? (
            <button
              id="account-page-edit-button"
              key="account-page-edit-button"
              aria-label="account-page-edit-button"
              data-test-id="account-page-edit-button"
              data-umami-event="account-page-edit-button-selected"
              data-umami-event-source="button"
              className="cursor-pointer rounded-full p-1 hover:bg-sky-600 hover:text-white"
              onClick={onClickEdit}
              type="button"
            >
              <MdEditOff size={22} className="ml-0.5" />
            </button>
          ) : (
            <button
              id="account-page-unedit-button"
              key="account-page-unedit-button"
              aria-label="account-page-unedit-button"
              data-test-id="account-page-unedit-button"
              data-umami-event="account-page-unedit-button-selected"
              className="cursor-pointer rounded-full p-1 hover:bg-sky-600 hover:text-white"
              onClick={onClickEdit}
              type="button"
            >
              <MdEdit size={22} className="ml-0.5" />
            </button>
          )}
        </div>
        <div className="flex min-h-full flex-col items-center justify-center gap-4 divide-solid divide-slate-300 md:flex-row">
          <div className="flex w-full flex-col items-center justify-center md:min-w-[264px] md:max-w-[320px]">
            <div className="mb-12 flex min-w-full flex-col items-center justify-center">
              <span className="relative">
                <button
                  id="account-page-edit-picture"
                  key="account-page-edit-picture"
                  aria-label="account-page-edit-picture"
                  data-test-id="account-page-edit-picture"
                  data-umami-event="account-page-edit-picture-selected"
                  onClick={onProfilePictureClick}
                  type="button"
                  className="group relative min-w-[264px] max-w-[264px] sm:max-w-[320px] cursor-pointer rounded-full"
                >
                  <div className="flex aspect-square min-w-full flex-col items-center justify-center rounded-full border-2 border-solid border-slate-300 bg-slate-300 shadow-sm">
                    {currentUser?.media?.profile_picture ? (
                      <Spinner color="fill-slate-300" />
                    ) : (
                      <IoIosPerson size={180} className="text-white/30" />
                    )}
                  </div>
                  {currentUser?.media?.profile_picture && (
                    <LazyBackground
                      className="absolute top-0 aspect-square min-w-full rounded-full border-2 border-solid border-slate-300 bg-slate-400 bg-cover shadow-sm"
                      src={`//${getEnvironmentVariable('REACT_APP_CDN_API')}${
                        currentUser?.media?.profile_picture
                      }`}
                    />
                  )}
                  <div className="absolute left-0 top-0 hidden aspect-square min-w-full flex-col items-center justify-center rounded-full border-2 border-solid border-slate-300 bg-slate-800/50 text-white group-hover:flex">
                    <TbPhotoEdit size={96} />
                  </div>
                </button>
                {currentUser?.media?.profile_picture && (
                  <button
                    className="absolute top-0 right-0 cursor-pointer hover:text-red-500"
                    type="button"
                    onClick={onDeletePicture}
                  >
                    <MdOutlineDelete size={26} />
                  </button>
                )}
              </span>
              <div className="mt-4 min-w-full">
                {onEdit ? (
                  <div className="flex min-w-full flex-col items-center justify-center gap-3">
                    <Input
                      id="firstName"
                      label={t('static.account.modal_input_firstname_label')}
                      disabled={false}
                      register={register}
                      errors={errors}
                    />
                    <Input
                      id="lastName"
                      label={t('static.account.modal_input_lastname_label')}
                      disabled={false}
                      register={register}
                      errors={errors}
                    />
                  </div>
                ) : (
                  <span className="min-w-full flex flex-col items-center justify-center">
                    <button
                      id="account-page-edit-name"
                      key="account-page-edit-name"
                      aria-label="account-page-edit-name"
                      data-test-id="account-page-edit-name"
                      data-umami-event="account-page-edit-button-selected"
                      data-umami-event-source="name"
                      className="cursor-pointer text-center text-lg font-semibold md:text-xl min-w-full"
                      onClick={onClickEdit}
                      type="button"
                    >
                      {currentUser?.first_name && currentUser?.last_name
                        ? `${currentUser?.first_name} ${currentUser?.last_name}`
                        : '-'}
                    </button>
                    {!isNil(currentUser) && (
                      <span className="line-clamp-1 overflow-hidden text-ellipsis font-bold text-lg cursor-pointer hover:underline drop-shadow-sm">
                        <Link
                          to={toLocalizedRoute(ROUTES.PRICES_ROUTE)}
                          className="cursor-pointer"
                        >
                          {t(
                            `static.logo.account_tier_name_${currentUser.account_tier}`,
                          )}
                        </Link>
                      </span>
                    )}
                  </span>
                )}
              </div>
              {!onEdit ? (
                <div className="mt-16 hidden w-full flex-col items-end justify-end gap-2 md:flex md:max-w-sm">
                  <span className="min-w-full">
                    <Button
                      disabled={false}
                      loading={false}
                      label={t('static.account.button_label_change_password')}
                      onClick={onChangePassword}
                      data-test-id="account-page-change-password-button"
                      id="account-page-change-password-button"
                    />
                  </span>
                  <span className="min-w-full">
                    <Button
                      disabled={false}
                      loading={false}
                      label={t('static.account.button_label_logout_account')}
                      onClick={onLogoutAccount}
                      data-test-id="account-page-logout-account-button"
                      id="account-page-logout-account-button"
                      outline
                    />
                  </span>
                </div>
              ) : (
                <div className="mt-4 hidden w-full flex-col items-end justify-end gap-2 md:flex md:max-w-sm">
                  <span className="min-w-full">
                    <Button
                      disabled={false}
                      loading={false}
                      label={t('static.account.button_label_delete_account')}
                      onClick={onDeleteAccount}
                      data-test-id="account-page-delete-account-button"
                      id="account-page-delete-account-button"
                      reject
                    />
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="mb-6 flex size-full grow flex-col items-center justify-start gap-16 md:pl-4">
            <div className="flex w-full flex-col gap-8">
              <div className="flex min-w-full flex-row items-center justify-between">
                <span className="flex h-full flex-col items-start justify-center text-start font-bold">
                  {t('static.account.modal_input_email_label')}
                </span>
                <span className="col-span-4 line-clamp-1 overflow-hidden text-ellipsis break-all text-right text-slate-500">
                  {currentUser ? currentUser.email : '-'}
                </span>
              </div>
              <div className="flex min-w-full flex-row items-center justify-between">
                <span className="flex h-full flex-col items-start justify-center text-start font-bold">
                  {t('static.account.modal_input_timezone_label')}
                </span>
                <span className="col-span-4 line-clamp-1 overflow-hidden text-ellipsis break-all text-right">
                  {onEdit ? (
                    <Select
                      data-test-id="timezone-selector"
                      placeholder={t('static.generic.selector_placeholder')}
                      isSearchable
                      menuPlacement="auto"
                      menuPosition="fixed"
                      menuShouldScrollIntoView
                      closeMenuOnSelect
                      options={allTimezones}
                      value={selectedTimezone}
                      isMulti={false}
                      onChange={(option: SelectorOptionType | null) => {
                        if (option) {
                          setSelectedTimezone(option);
                        }
                      }}
                      formatOptionLabel={createSelectLabel}
                      classNames={{
                        control: () => 'p-3 border-2',
                        input: () => 'text-lg',
                        option: () => 'text-lg',
                      }}
                      theme={(theme) => ({
                        ...theme,
                        borderRadius: 6,
                        colors: {
                          ...theme.colors,
                          primary: '#083344',
                          primary25: '#ecfeff',
                        },
                      })}
                    />
                  ) : (
                    <button
                      id="account-page-edit-timezone"
                      key="account-page-edit-timezone"
                      aria-label="account-page-edit-timezone"
                      data-test-id="account-page-edit-timezone"
                      data-umami-event="account-page-edit-button-selected"
                      data-umami-event-source="timezone"
                      className="cursor-pointer"
                      onClick={onClickEdit}
                      type="button"
                    >
                      {currentUser?.timezone ? currentUser.timezone : '-'}
                    </button>
                  )}
                </span>
              </div>
              <div className="flex min-w-full flex-row items-center justify-between">
                <span className="flex h-full flex-col items-start justify-center text-start font-bold">
                  {t('static.account.modal_input_language_label')}
                </span>
                <span className="col-span-4 line-clamp-1 overflow-hidden text-ellipsis break-all text-right">
                  {onEdit ? (
                    <Select
                      data-test-id="language-selector"
                      placeholder={t('static.generic.selector_placeholder')}
                      isSearchable
                      menuPlacement="auto"
                      menuPosition="fixed"
                      menuShouldScrollIntoView
                      closeMenuOnSelect
                      options={allLanguages}
                      value={selectedLanguage}
                      isMulti={false}
                      onChange={(option: SelectorOptionType | null) => {
                        if (option) {
                          setSelectedLanguage(option);
                        }
                      }}
                      formatOptionLabel={createSelectLabel}
                      classNames={{
                        control: () => 'p-3 border-2',
                        input: () => 'text-lg',
                        option: () => 'text-lg',
                      }}
                      theme={(theme) => ({
                        ...theme,
                        borderRadius: 6,
                        colors: {
                          ...theme.colors,
                          primary: '#083344',
                          primary25: '#ecfeff',
                        },
                      })}
                    />
                  ) : (
                    <button
                      id="account-page-edit-language"
                      key="account-page-edit-language"
                      aria-label="account-page-edit-language"
                      data-test-id="account-page-edit-language"
                      data-umami-event="account-page-edit-button-selected"
                      data-umami-event-source="language"
                      className="cursor-pointer"
                      onClick={onClickEdit}
                      type="button"
                    >
                      {currentUser?.language
                        ? t(
                            `static.language.language_name_${currentUser.language.toLowerCase()}`,
                          )
                        : '-'}
                    </button>
                  )}
                </span>
              </div>
              <div className="flex min-w-full flex-row items-center justify-between">
                <span className="flex h-full flex-col items-start justify-center text-start font-bold">
                  {t('static.account.modal_input_currency_label')}
                </span>
                <span className="col-span-4 line-clamp-1 overflow-hidden text-ellipsis break-all text-right">
                  {onEdit ? (
                    <Select
                      data-test-id="currency-selector"
                      placeholder={t('static.generic.selector_placeholder')}
                      isSearchable
                      menuPlacement="auto"
                      menuPosition="fixed"
                      menuShouldScrollIntoView
                      closeMenuOnSelect
                      options={allCurrencies}
                      value={selectedCurrency}
                      isMulti={false}
                      onChange={(option: SelectorOptionType | null) => {
                        if (option) {
                          setSelectedCurrency(option);
                        }
                      }}
                      formatOptionLabel={createSelectLabel}
                      classNames={{
                        control: () => 'p-3 border-2',
                        input: () => 'text-lg',
                        option: () => 'text-lg',
                      }}
                      theme={(theme) => ({
                        ...theme,
                        borderRadius: 6,
                        colors: {
                          ...theme.colors,
                          primary: '#083344',
                          primary25: '#ecfeff',
                        },
                      })}
                    />
                  ) : (
                    <button
                      id="account-page-edit-currency"
                      key="account-page-edit-currency"
                      aria-label="account-page-edit-currency"
                      data-test-id="account-page-edit-currency"
                      data-umami-event="account-page-edit-button-selected"
                      data-umami-event-source="currency"
                      className="cursor-pointer"
                      onClick={onClickEdit}
                      type="button"
                    >
                      {currentUser?.currency ? currentUser.currency : '-'}
                    </button>
                  )}
                </span>
              </div>
            </div>
            <PreferencesBox />
          </div>
        </div>
        <div className="my-8 flex min-w-full flex-col items-center justify-center text-center sm:items-end sm:justify-end sm:text-end">
          {onEdit ? (
            <div className="flex min-w-full flex-col items-end justify-end gap-2 sm:max-w-md sm:flex-row">
              <Button
                disabled={false}
                loading={false}
                label={t('static.account.button_label_cancel')}
                onClick={onCancel}
                data-test-id="account-page-cancel-button"
                id="account-page-cancel-button"
                outline
              />
              <Button
                disabled={false}
                loading={false}
                label={t('static.account.button_label_save')}
                onClick={handleSubmit(onSave)}
                data-test-id="account-page-save-button"
                id="account-page-save-button"
              />
              <Button
                disabled={false}
                loading={false}
                label={t('static.account.button_label_delete_account')}
                onClick={onDeleteAccount}
                data-test-id="account-page-delete-account-button"
                id="account-page-delete-account-button"
                className="block md:hidden"
                reject
              />
            </div>
          ) : (
            <div className="flex min-w-full flex-col items-end justify-end gap-2 md:hidden md:max-w-md md:flex-row">
              <Button
                disabled={false}
                loading={false}
                label={t('static.account.button_label_change_password')}
                onClick={onChangePassword}
                data-test-id="account-page-change-password-button"
                id="account-page-change-password-button"
              />
              <Button
                disabled={false}
                loading={false}
                label={t('static.account.button_label_logout_account')}
                onClick={onLogoutAccount}
                data-test-id="account-page-logout-account-button"
                id="account-page-logout-account-button"
                outline
              />
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Account;
